
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator"; 
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Euro, Package, Trash2 } from "lucide-react";
import { useUserRoles } from "@/hooks/useUserRoles";

interface GuardEquipmentDetailsProps {
  guardId: string;
}

interface EquipmentItem {
  id: string;
  equipmentName: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  assignedAt: string;
  notes: string | null;
}

export const GuardEquipmentDetails = ({ guardId }: GuardEquipmentDetailsProps) => {
  const [equipment, setEquipment] = useState<EquipmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  
  useEffect(() => {
    if (guardId) {
      fetchGuardEquipment();
    }
  }, [guardId]);
  
  const fetchGuardEquipment = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('guard_equipment')
        .select(`
          id,
          quantity,
          assigned_date,
          notes,
          equipment_types:equipment_type_id(id, name, base_price)
        `)
        .eq('guard_id', guardId)
        .eq('status', 'assigned');
        
      if (error) throw error;
      
      const equipmentData: EquipmentItem[] = data.map(item => ({
        id: item.id,
        equipmentName: item.equipment_types?.name || 'Unknown',
        quantity: item.quantity || 0,
        unitCost: item.equipment_types?.base_price || 0,
        totalCost: (item.quantity || 0) * (item.equipment_types?.base_price || 0),
        assignedAt: new Date(item.assigned_date).toLocaleDateString(),
        notes: item.notes
      }));
      
      setEquipment(equipmentData);
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load guard equipment: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRemoveEquipment = async (itemId: string, equipmentName: string, quantity: number) => {
    try {
      setIsRemoving(prev => ({ ...prev, [itemId]: true }));
      
      // First, mark the equipment as returned
      const { error: returnError } = await supabase
        .from('guard_equipment')
        .update({
          status: 'returned',
          return_date: new Date().toISOString()
        })
        .eq('id', itemId);
        
      if (returnError) throw returnError;
      
      // Remove the item from the local state
      setEquipment(prev => prev.filter(item => item.id !== itemId));
      
      toast({
        title: "Equipment Removed",
        description: `${equipmentName} has been removed from this guard.`,
      });
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to remove equipment: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsRemoving(prev => ({ ...prev, [itemId]: false }));
    }
  };
  
  const calculateTotalCost = () => {
    return equipment.reduce((acc, item) => acc + item.totalCost, 0);
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Package className="h-5 w-5 mr-2" /> Equipment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center py-4 text-muted-foreground">Loading equipment data...</p>
        </CardContent>
      </Card>
    );
  }
  
  if (equipment.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Package className="h-5 w-5 mr-2" /> Equipment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center py-4 text-muted-foreground">No equipment assigned to this guard.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <Package className="h-5 w-5 mr-2" /> Assigned Equipment
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Equipment</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit Cost</TableHead>
                <TableHead className="text-right">Total</TableHead>
                {isAdmin && <TableHead className="w-[100px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {equipment.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.equipmentName}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">€{item.unitCost.toFixed(2)}</TableCell>
                  <TableCell className="text-right">€{item.totalCost.toFixed(2)}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleRemoveEquipment(item.id, item.equipmentName, item.quantity)}
                        disabled={isRemoving[item.id]}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={isAdmin ? 3 : 3} className="text-right font-semibold">Total Cost</TableCell>
                <TableCell className="text-right font-semibold">€{calculateTotalCost().toFixed(2)}</TableCell>
                {isAdmin && <TableCell></TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
      
      {equipment.some(item => item.notes) && (
        <>
          <Separator className="my-4" />
          <CardContent>
            <h4 className="font-medium mb-2">Notes:</h4>
            <ul className="space-y-2">
              {equipment
                .filter(item => item.notes)
                .map((item, index) => (
                  <li key={index} className="text-sm">
                    <Badge variant="outline" className="mr-2">{item.equipmentName}</Badge>
                    {item.notes}
                  </li>
                ))}
            </ul>
          </CardContent>
        </>
      )}
    </Card>
  );
};
